import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CALL_CONTEXT_SCOPE_OPTIONS, resolveCallContext } from '../callContextConfig';
import './CallContextChip.css';

/**
 * CallContextChip — sits next to the Send button when the selected template
 * uses Call Context. Shows before generating which logged calls the email
 * will pick up, or why none qualify.
 */

const ACTIVITY_LABEL = { call: 'Call', meeting: 'Meeting', voicemail: 'Voicemail' };
const OUTCOME_LABEL = {
  connected: 'Reached',
  meeting_scheduled: 'Meeting scheduled',
  send_info: 'Send info',
  not_interested: 'Not interested',
  no_answer: 'No answer',
};

const toMs = (v) => {
  if (!v) return 0;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  const ms = typeof v === 'number' ? v : Date.parse(v);
  return Number.isFinite(ms) ? ms : 0;
};

const formatDate = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
  } catch (_) {
    return '';
  }
};

const COPY = {
  ok: {
    tone: 'ok',
    icon: 'fa-phone',
    head: (n) => `${n} call note${n === 1 ? '' : 's'} will be used`,
  },
  missing: {
    tone: 'warn',
    icon: 'fa-phone-slash',
    head: () => 'No matching call note',
  },
  not_interested: {
    tone: 'warn',
    icon: 'fa-ban',
    head: () => 'Last call: not interested',
  },
};

export default function CallContextChip({ contact, topic }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const resolved = useMemo(
    () => resolveCallContext({ contact, topic }),
    [contact, topic],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (resolved.status === 'off') return null;
  const copy = COPY[resolved.status];
  const { config, notes } = resolved;
  const scopeLabel = CALL_CONTEXT_SCOPE_OPTIONS.find((o) => o.id === config.scope)?.label || '';
  const head = copy.head(notes.length);

  return (
    <div className="ccc-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ccc-trigger ccc-trigger--${copy.tone} ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title={head}
      >
        <i className={`fa-solid ${copy.icon}`} />
        {resolved.status === 'ok' && <span className="ccc-count">{notes.length}</span>}
      </button>

      {open && (
        <div className={`ccc-popover ccc-popover--${copy.tone}`}>
          <div className="ccc-head">
            <i className={`fa-solid ${copy.icon}`} />
            <span>{head}</span>
          </div>

          {resolved.status === 'ok' && (
            <>
              <ul className="ccc-list">
                {notes.map((n, i) => (
                  <li key={i} className="ccc-item">
                    <span className="ccc-item-meta">
                      {[formatDate(toMs(n.createdAt)), ACTIVITY_LABEL[n.activity], OUTCOME_LABEL[n.outcome]]
                        .filter(Boolean).join(' · ')}
                    </span>
                    {n.text ? <span className="ccc-item-text">{n.text}</span> : null}
                  </li>
                ))}
              </ul>
              <p className="ccc-text">
                The template writes the email with these notes. To leave a note out, tick
                &ldquo;Don&rsquo;t use in AI emails&rdquo; in the contact&rsquo;s Notes.
              </p>
            </>
          )}

          {resolved.status === 'missing' && (
            <p className="ccc-text">
              This template uses Call Context ({scopeLabel.toLowerCase()}, max. {config.maxAgeDays} days),
              but no logged call matches. The email is written without call context.
              {config.whenMissing === 'hold' && ' Agents and flows hold this contact instead of sending.'}
            </p>
          )}

          {resolved.status === 'not_interested' && (
            <p className="ccc-text">
              The latest logged call says the contact is not interested. Agents and flows
              don&rsquo;t send this template to them. Check before you send it by hand.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
